import { AddRecipientField } from "@/components/campaignBuilder/recipients/AddRecipientField";
import type { RecipientFormErrors, RecipientFormValue } from "@/components/contacts/types";
import CountryDropdown from "@/components/giftExperience/CountryDropdown";
import { COPY } from "@/constants/campaignContacts";

/** Above the add-recipient wizard (MUI dialogs sit at 1300). */
const COUNTRY_MENU_Z_INDEX = 10001;

interface RecipientDetailsFormProps {
  idPrefix: string;
  value: RecipientFormValue;
  errors: RecipientFormErrors;
  onChange: (value: RecipientFormValue) => void;
  automation: boolean;
  minSendDate: string;
  maxSendDate?: string;
  disabled?: boolean;
}

const SECTION_CLASS =
  "font-lato text-[12px] font-semibold text-graphite uppercase tracking-[0.8px]";

type TextKey = Exclude<keyof RecipientFormValue, "country" | "sendDate">;

export default function RecipientDetailsForm({
  idPrefix,
  value,
  errors,
  onChange,
  automation,
  minSendDate,
  maxSendDate,
  disabled = false,
}: RecipientDetailsFormProps) {
  const T = COPY.recipientForm;

  const text = (key: TextKey, label: string, required: boolean, type = "text") => (
    <AddRecipientField
      id={`${idPrefix}-${key}`}
      label={label}
      required={required}
      showLabel
      fullWidth
      type={type}
      value={value[key]}
      onChange={(e) => onChange({ ...value, [key]: e.target.value })}
      error={errors[key]}
    />
  );

  return (
    <fieldset disabled={disabled} className="m-0 flex min-w-0 flex-col gap-4 border-0 p-0">
      <span className={SECTION_CLASS}>{T.recipientSection}</span>
      {text("firstName", T.firstName, true)}
      {text("lastName", T.lastName, false)}
      {text("email", T.email, true, "email")}
      <div className="flex w-full flex-col gap-1.5">
        <span className={SECTION_CLASS}>{T.country} *</span>
        <CountryDropdown
          selectedCountry={value.country}
          onChange={(code) => onChange({ ...value, country: code })}
          fullWidth
          menuZIndex={COUNTRY_MENU_Z_INDEX}
        />
        {errors.country && <p className="font-lato text-[12px] text-good-wine">{errors.country}</p>}
      </div>
      <span className={SECTION_CLASS}>{T.senderSection}</span>
      {text("senderFirstName", T.senderFirstName, false)}
      {text("senderLastName", T.senderLastName, false)}
      {text("senderCompany", T.senderCompany, false)}
      {automation && (
        <AddRecipientField
          id={`${idPrefix}-sendDate`}
          label={T.sendDate}
          required
          showLabel
          fullWidth
          type="date"
          value={value.sendDate}
          min={minSendDate}
          max={maxSendDate}
          onChange={(e) => onChange({ ...value, sendDate: e.target.value })}
          error={errors.sendDate}
        />
      )}
    </fieldset>
  );
}
